import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import {
	contentControllerGetContentMetaDataByActivePage,
	type MarkdownActiveMetaDataWord,
} from "@/shared/api/generated"

const BUFFER_PAGES = 1
const KEEP_RADIUS = BUFFER_PAGES + 2

interface UseMarkdownWordsResult {
	/** Aligned words of the loaded window, flat, sorted by start_ts */
	words: MarkdownActiveMetaDataWord[]
	/** Lookup for click-to-seek: wordIdx -> word */
	wordByIdx: Map<number, MarkdownActiveMetaDataWord>
	/** Re-fetch the current window (processing mode: new chunks landed) */
	invalidate: () => void
}

/**
 * Windowed word-timing metadata for the markdown viewer — mirror of the PDF
 * viewer's usePageMeta. Only pages around the audio page and the
 * scroll-visible page are kept in memory; far pages are evicted, so a large
 * document never has all its word data loaded at once.
 *
 * Completed content: per-page combined JSONs (immutable, browser-cached).
 * Processing content: 3-page windows from the active-page endpoint.
 */
export const useMarkdownWords = (
	contentId: string,
	audioPage: number,
	scrollPage: number,
	combinedUrls?: string[]
): UseMarkdownWordsResult => {
	const [pageMap, setPageMap] = useState<
		Map<number, MarkdownActiveMetaDataWord[]>
	>(new Map())
	const [fetchVersion, setFetchVersion] = useState(0)
	const loadedPagesRef = useRef<Set<number>>(new Set())
	/** Per-page abort controllers — only cancel requests for pages no longer needed */
	const inFlightRef = useRef<Map<number, AbortController>>(new Map())

	const isCompleted = !!combinedUrls?.length

	const storePages = useCallback(
		(results: Map<number, MarkdownActiveMetaDataWord[]>) => {
			if (results.size === 0) return
			for (const k of results.keys()) loadedPagesRef.current.add(k)
			setPageMap(prev => {
				const merged = new Map(prev)
				for (const [k, v] of results) merged.set(k, v)
				return merged
			})
		},
		[]
	)

	useEffect(() => {
		const needed = new Set<number>()

		if (audioPage >= 0) {
			for (let i = audioPage - BUFFER_PAGES; i <= audioPage + BUFFER_PAGES; i++) {
				if (i >= 0) needed.add(i)
			}
		}
		if (scrollPage >= 0) {
			for (let i = scrollPage - BUFFER_PAGES; i <= scrollPage + BUFFER_PAGES; i++) {
				if (i >= 0) needed.add(i)
			}
		}

		// Evict pages far from both centers
		const keepSet = new Set<number>()
		if (audioPage >= 0) {
			for (let i = audioPage - KEEP_RADIUS; i <= audioPage + KEEP_RADIUS; i++) {
				if (i >= 0) keepSet.add(i)
			}
		}
		if (scrollPage >= 0) {
			for (let i = scrollPage - KEEP_RADIUS; i <= scrollPage + KEEP_RADIUS; i++) {
				if (i >= 0) keepSet.add(i)
			}
		}

		// Abort in-flight requests ONLY for pages that left the keep radius
		for (const [page, controller] of inFlightRef.current) {
			if (!keepSet.has(page)) {
				controller.abort()
				inFlightRef.current.delete(page)
			}
		}

		// Remove evicted pages from tracking and state
		for (const p of loadedPagesRef.current) {
			if (!keepSet.has(p)) loadedPagesRef.current.delete(p)
		}
		setPageMap(prev => {
			let evicted = false
			for (const k of prev.keys()) {
				if (!keepSet.has(k)) {
					evicted = true
					break
				}
			}
			if (!evicted) return prev
			const cleaned = new Map<number, MarkdownActiveMetaDataWord[]>()
			for (const [k, v] of prev) {
				if (keepSet.has(k)) cleaned.set(k, v)
			}
			return cleaned
		})

		// Filter out pages already loaded or already in-flight
		const toLoad = (
			fetchVersion > 0
				? [...needed]
				: [...needed].filter(p => !loadedPagesRef.current.has(p))
		).filter(p => !inFlightRef.current.has(p))

		if (toLoad.length === 0) return

		if (isCompleted) {
			// Completed mode: each page fetched independently in parallel
			for (const pageIndex of toLoad) {
				if (!combinedUrls || pageIndex >= combinedUrls.length) continue
				const url = combinedUrls[pageIndex]
				if (!url) continue

				const controller = new AbortController()
				inFlightRef.current.set(pageIndex, controller)

				axios
					.get<MarkdownActiveMetaDataWord[]>(url, {
						signal: controller.signal,
						headers: { "Cache-Control": "public, max-age=31536000" },
					})
					.then(response => {
						storePages(new Map([[pageIndex, response.data]]))
					})
					.catch(e => {
						if (e instanceof DOMException && e.name === "AbortError") return
						console.error("Failed to fetch markdown word meta:", e)
					})
					.finally(() => {
						inFlightRef.current.delete(pageIndex)
					})
			}
		} else {
			// Processing mode: each center page fetched independently in parallel
			for (const center of toLoad) {
				const controller = new AbortController()
				inFlightRef.current.set(center, controller)

				contentControllerGetContentMetaDataByActivePage(
					contentId,
					{ pageIndex: String(center) },
					{ signal: controller.signal }
				)
					.then(response => {
						if (response.viewMode !== "MARKDOWN") return
						const results = new Map<number, MarkdownActiveMetaDataWord[]>()
						for (const page of [
							response.prevPageWords,
							response.currentPageWords,
							response.nextPageWords,
						]) {
							if (page.words.length) results.set(page.pageIndex, page.words)
						}
						storePages(results)
					})
					.catch(e => {
						if (e instanceof DOMException && e.name === "AbortError") return
						console.error("Failed to fetch markdown word meta:", e)
					})
					.finally(() => {
						inFlightRef.current.delete(center)
					})
			}
		}
	}, [
		contentId,
		audioPage,
		scrollPage,
		fetchVersion,
		isCompleted,
		combinedUrls,
		storePages,
	])

	// Cleanup all in-flight on unmount
	useEffect(() => {
		return () => {
			for (const controller of inFlightRef.current.values()) {
				controller.abort()
			}
			inFlightRef.current.clear()
		}
	}, [])

	const invalidate = useCallback(() => {
		loadedPagesRef.current.clear()
		setFetchVersion(v => v + 1)
	}, [])

	// ─── Flat sorted list + lookup over the loaded window ────

	const words = useMemo(() => {
		const all: MarkdownActiveMetaDataWord[] = []
		const pageIndexes = [...pageMap.keys()].sort((a, b) => a - b)
		for (const pageIndex of pageIndexes) {
			all.push(...(pageMap.get(pageIndex) ?? []))
		}
		all.sort((a, b) => a.start_ts - b.start_ts)
		return all
	}, [pageMap])

	const wordByIdx = useMemo(() => {
		const map = new Map<number, MarkdownActiveMetaDataWord>()
		for (const word of words) {
			if (word.wordIdx !== null && word.wordIdx !== undefined) {
				map.set(word.wordIdx, word)
			}
		}
		return map
	}, [words])

	return { words, wordByIdx, invalidate }
}
